import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, Upload, Trash2, Download, File, Image, FileText, Music, Video as VideoIcon, FileSpreadsheet, FileArchive, Loader2, FolderOpen } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useUserAssetsStore } from '@/stores/userAssetsStore';
import { getUserAssets, createUserAsset, deleteUserAsset, uploadAssetFile, getAssetSignedUrl, UserAsset, markVideoNoResourcesNeeded } from '@/services/data/userAssets';
import { toast } from 'sonner';

interface ResourcesSidePanelProps {
  readonly videoId: string;
  readonly videoTitle: string;
  readonly onClose: () => void;
  readonly onResourcesUpdated?: () => void;
}

const getFileIcon = (fileType: string | null) => {
  if (!fileType) return File;
  if (fileType.startsWith('image/')) return Image;
  if (fileType.startsWith('video/')) return VideoIcon;
  if (fileType.startsWith('audio/')) return Music;
  if (fileType.includes('pdf') || fileType.includes('document')) return FileText;
  if (fileType.includes('spreadsheet') || fileType.includes('excel')) return FileSpreadsheet;
  if (fileType.includes('zip') || fileType.includes('archive')) return FileArchive;
  return File;
};

const formatFileSize = (bytes: number | null) => {
  if (!bytes) return 'Taille inconnue';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Determines the button label based on current state
 */
function getValidationButtonLabel(
  markingComplete: boolean,
  noResourcesNeeded: boolean,
  hasAssets: boolean
): string {
  if (markingComplete) return '';
  if (noResourcesNeeded) {
    return hasAssets ? 'Ressources validées ✓' : 'Aucune ressource ✓';
  }
  return hasAssets ? 'Valider mes ressources' : 'Je n\'ai pas de ressources';
}

/**
 * Hook for managing assets loading
 */
function useAssetsLoader(userId: string | null, videoId: string) {
  const [assets, setAssets] = useState<UserAsset[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadAssets = async () => {
      if (!userId) return;
      setIsLoading(true);

      try {
        const videoAssets = await getUserAssets(userId, videoId);
        setAssets(videoAssets);
      } catch (error) {
        console.error('Error loading assets:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAssets();
  }, [userId, videoId]);

  return { assets, setAssets, isLoading };
}

/**
 * Hook for file upload handling
 */
function useFileUpload(
  userId: string | null,
  videoId: string,
  setAssets: React.Dispatch<React.SetStateAction<UserAsset[]>>,
  refreshPendingResources: (userId: string) => Promise<void>,
  onResourcesUpdated?: () => void
) {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !userId) return;

    setIsUploading(true);

    try {
      for (const file of Array.from(files)) {
        const uploadResult = await uploadAssetFile(userId, file);

        const newAsset = await createUserAsset({
          user_id: userId,
          folder_id: videoId,
          file_name: file.name,
          file_path: uploadResult.filePath,
          file_type: file.type,
          file_size: file.size,
        });

        setAssets(prev => [newAsset, ...prev]);
      }

      toast.success('Fichier(s) uploadé(s) avec succès');
      await refreshPendingResources(userId);
      onResourcesUpdated?.();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  }, [userId, videoId, setAssets, refreshPendingResources, onResourcesUpdated]);

  return { isUploading, handleFileUpload };
}

/**
 * Hook for asset operations (delete, download)
 */
function useAssetOperations(
  userId: string | null,
  setAssets: React.Dispatch<React.SetStateAction<UserAsset[]>>,
  onResourcesUpdated?: () => void
) {
  const [deletingAssetId, setDeletingAssetId] = useState<string | null>(null);
  const [downloadingAssetId, setDownloadingAssetId] = useState<string | null>(null);

  const handleDeleteAsset = useCallback(async (asset: UserAsset) => {
    if (!userId) return;

    setDeletingAssetId(asset.id);

    try {
      await deleteUserAsset(asset.id);
      setAssets(prev => prev.filter(a => a.id !== asset.id));
      toast.success('Fichier supprimé');
      onResourcesUpdated?.();
    } catch (error) {
      console.error('Error deleting asset:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingAssetId(null);
    }
  }, [userId, setAssets, onResourcesUpdated]);

  const handleDownloadAsset = useCallback(async (asset: UserAsset) => {
    setDownloadingAssetId(asset.id);

    try {
      const url = await getAssetSignedUrl(asset.file_path);
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Impossible de télécharger le fichier');
      }
    } catch (error) {
      console.error('Error downloading asset:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingAssetId(null);
    }
  }, []);

  return { deletingAssetId, downloadingAssetId, handleDeleteAsset, handleDownloadAsset };
}

/**
 * Asset item component to reduce complexity in main render
 */
interface AssetItemProps {
  readonly asset: UserAsset;
  readonly isDeleting: boolean;
  readonly isDownloading: boolean;
  readonly onDownload: (asset: UserAsset) => void;
  readonly onDelete: (asset: UserAsset) => void;
}

function AssetItem({ asset, isDeleting, isDownloading, onDownload, onDelete }: AssetItemProps) {
  const FileIcon = getFileIcon(asset.file_type);

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
      <FileIcon className="h-5 w-5 text-white/50 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">{asset.file_name}</p>
        <p className="text-xs text-white/40">{formatFileSize(asset.file_size)}</p>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onDownload(asset)}
          disabled={isDownloading}
          className="p-2 text-white/40 hover:text-white/70 transition-colors disabled:opacity-50"
        >
          {isDownloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </button>
        <button
          onClick={() => onDelete(asset)}
          disabled={isDeleting}
          className="p-2 text-white/40 hover:text-red-400 transition-colors disabled:opacity-50"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

/**
 * Validation button component
 */
interface ValidationButtonProps {
  readonly hasAssets: boolean;
  readonly markingComplete: boolean;
  readonly noResourcesNeeded: boolean;
  readonly onValidate: () => Promise<void>;
}

function ValidationButton({ hasAssets, markingComplete, noResourcesNeeded, onValidate }: ValidationButtonProps) {
  const buttonLabel = getValidationButtonLabel(markingComplete, noResourcesNeeded, hasAssets);

  const baseClassName = "w-full py-3 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-xl flex items-center justify-center gap-2";
  const className = hasAssets
    ? `${baseClassName} text-black bg-amber-500 hover:bg-amber-600`
    : `${baseClassName} text-white/70 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06]`;

  return (
    <button
      onClick={onValidate}
      disabled={markingComplete || noResourcesNeeded}
      className={className}
    >
      {markingComplete ? <Loader2 className="h-4 w-4 animate-spin" /> : buttonLabel}
    </button>
  );
}

export function ResourcesSidePanel({ videoId, videoTitle, onClose, onResourcesUpdated }: ResourcesSidePanelProps) {
  const { userId } = useAuthStore();
  const { videoContents, loadInitialData, refreshPendingResources } = useUserAssetsStore();
  const [markingComplete, setMarkingComplete] = useState(false);
  const [noResourcesNeeded, setNoResourcesNeeded] = useState(false);

  const videoContent = videoContents.find(v => v.id === videoId);
  const { assets, setAssets, isLoading } = useAssetsLoader(userId, videoId);
  const { isUploading, handleFileUpload } = useFileUpload(userId, videoId, setAssets, refreshPendingResources, onResourcesUpdated);
  const { deletingAssetId, downloadingAssetId, handleDeleteAsset, handleDownloadAsset } = useAssetOperations(userId, setAssets, onResourcesUpdated);

  useEffect(() => {
    setNoResourcesNeeded(videoContent?.resources_not_needed === true);
  }, [videoContent?.resources_not_needed]);

  const handleMarkNoResourcesNeeded = useCallback(async (): Promise<boolean> => {
    if (!videoContent) return false;

    setMarkingComplete(true);

    try {
      await markVideoNoResourcesNeeded(videoId);
      toast.success('Vidéo marquée comme complète');
      onResourcesUpdated?.();
      if (userId) {
        await loadInitialData(userId);
      }
      onClose();
      return true;
    } catch (error) {
      console.error('Error marking video complete:', error);
      toast.error('Erreur lors de la mise à jour');
      return false;
    } finally {
      setMarkingComplete(false);
    }
  }, [videoContent, videoId, userId, loadInitialData, onClose, onResourcesUpdated]);

  const handleValidateAndClose = useCallback(async () => {
    const ok = await handleMarkNoResourcesNeeded();
    if (ok) onClose();
  }, [handleMarkNoResourcesNeeded, onClose]);

  return (
    <div className="h-full flex flex-col">
      {/* Header with back button */}
      <div className="px-6 py-5 border-b border-white/[0.04]">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-white/60 hover:text-white/80 transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
          <span className="text-sm font-medium">Retour</span>
        </button>
      </div>

      {/* Title */}
      <div className="px-6 py-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <FolderOpen className="h-5 w-5 text-amber-400" />
          </div>
          <h2 className="text-lg font-semibold text-white line-clamp-1">{videoTitle}</h2>
        </div>
        <p className="text-sm text-white/50">
          Ajoutez les ressources nécessaires pour cette vidéo
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 text-white/40 animate-spin" />
          </div>
        ) : (
          <>
            {/* Upload button */}
            <div className="mb-4">
              <label className="w-full flex items-center justify-center gap-2 px-4 py-4 rounded-xl bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 transition-all cursor-pointer">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />
                {isUploading ? (
                  <Loader2 className="h-5 w-5 text-amber-400 animate-spin" />
                ) : (
                  <>
                    <Upload className="h-5 w-5 text-amber-400" />
                    <span className="text-sm font-medium text-amber-400">Ajouter des fichiers</span>
                  </>
                )}
              </label>
            </div>

            {/* Assets list */}
            {assets.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-white/40 text-sm">Aucun fichier uploadé</p>
              </div>
            ) : (
              <div className="space-y-2">
                {assets.map(asset => (
                  <AssetItem
                    key={asset.id}
                    asset={asset}
                    isDeleting={deletingAssetId === asset.id}
                    isDownloading={downloadingAssetId === asset.id}
                    onDownload={handleDownloadAsset}
                    onDelete={handleDeleteAsset}
                  />
                ))}
              </div>
            )}

            {/* Validation buttons */}
            <div className="mt-6 pt-6 border-t border-white/[0.04] space-y-3">
              <ValidationButton
                hasAssets={assets.length > 0}
                markingComplete={markingComplete}
                noResourcesNeeded={noResourcesNeeded}
                onValidate={handleValidateAndClose}
              />

              {!noResourcesNeeded && (
                <button
                  onClick={onClose}
                  className="w-full py-2.5 text-sm font-medium text-white/50 hover:text-white/70 transition-colors"
                >
                  Fermer
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}